/**
 * Flint brand block — the "F" mark (with sky accent bar) + name + tagline.
 *
 * Visual: 30×30 dark-ink square, white bold F, plus a 2px sky bar at the
 * bottom of the mark — that's the prototype's `.brand-mark::after` accent.
 * Drop the bar by passing `accent={false}` for static-logo contexts.
 *
 * The `tagline` is the small line under the wordmark — defaults to the corporate
 * "Flint"; the workspace topbar passes the product motto ("ERP") so the
 * lockup reads "Flint ERP".
 *
 * `BrandSquare` is the mark alone, exposed for surfaces that scale it
 * (the launcher's hero hearth uses the 64px `hero` size).
 *
 * @see prototype/sales/customers.html `.brand` / `.brand-mark` — V1.1 baseline
 * @see prototype/explorations/module-launcher.html `.logo-mark` — hero size
 */
import { cn } from "@flint/ui";

export interface BrandSquareProps {
  /** Hide the small sky accent at the bottom of the mark. */
  accent?: boolean;
  /** `sm` 30px (chrome lockup) or `hero` 64px (launcher hearth). */
  size?: "sm" | "hero";
  className?: string;
}

export function BrandSquare({ accent = true, size = "sm", className }: BrandSquareProps) {
  const hero = size === "hero";
  return (
    <div
      aria-hidden
      className={cn(
        "relative grid place-items-center bg-ink text-paper font-extrabold",
        hero
          ? "h-16 w-16 rounded-[14px] text-3xl shadow-md"
          : "h-[30px] w-[30px] rounded-sm text-base shadow-sm",
        className,
      )}
    >
      F
      {accent ? (
        <span
          aria-hidden
          className={cn(
            "absolute bg-sky-600 rounded-full",
            hero ? "left-3.5 right-3.5 bottom-2 h-[3px]" : "left-1.5 right-1.5 bottom-1 h-0.5",
          )}
        />
      ) : null}
    </div>
  );
}

export interface BrandMarkProps {
  /** Hide the small sky accent at the bottom of the mark. */
  accent?: boolean;
  /** Small line under the "Flint" wordmark. */
  tagline?: string;
}

export function BrandMark({ accent = true, tagline = "Flint" }: BrandMarkProps) {
  return (
    <div className="flex items-center gap-2.5">
      <BrandSquare accent={accent} />
      <div className="flex flex-col leading-tight">
        <span className="text-base font-bold text-ink tracking-tight">Flint</span>
        <span className="text-xs text-muted -mt-0.5">{tagline}</span>
      </div>
    </div>
  );
}
